import {
  useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Lock, Info, AlertTriangle, CheckCircle2, Sparkles, PenLine, Heart, CalendarCheck2, ArrowLeftRight,
  Eye, EyeOff, X,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { useLabel } from '@/lib/labels';
import { formatDateLabel, formatCurrency, formatElapsedDuration } from '@/lib/format';
import { dayTextClass, holidayNameOf, resolveDayKind } from '@/lib/calendar-day';
import { useHolidays } from '@/hooks/useHolidays';
import { usePreferenceInputMode } from '@/hooks/usePreferenceInputMode';
import { useShiftPatternPresets } from '@/hooks/useShiftPatternPresets';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { DateLabel } from '@/components/DateLabel';
import { Modal } from '@/components/Modal';
import { LaborCostConfidenceToggle } from '@/components/LaborCostConfidenceToggle';
import { LABOR_COST_CONFIDENCE_LABELS, type LaborCostConfidence } from '@/lib/shift-report-labels';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { TimeRangeInput, type TimeRangeInputValue } from '@/components/TimeRangeInput';
import { personKey, isoToLocalHm, buildLocalIso, DRAFT_STATUS_LABELS, ORIGIN_LABELS, type PersonRef } from '@/lib/shift-format';
import { resolveTimeBand, TIME_BAND_COLOR_CLASSES, TIME_BAND_ICON_COMPONENTS, type TimeBand } from '@/lib/time-band';
import { buildRowStats, matchesBoardFilter, isBoardFilterActive, EMPTY_BOARD_FILTER, type BoardFilterState } from '@/lib/shift-board-filters';
import { ViolationDetail } from '@/components/ViolationDetail';

/**
 * `/shift/periods/:id/board` (v0.4 章④/⑥ F-2、D-55/D-56/§4-11b)。v0.4 で最も難度が高い1画面。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-8〜§4-11 / §6-1〜§6-3
 *
 * ⚠️ §4-11b の3幅縮退形について、本実装では ≥768px を1つの「グリッド表示」(sticky 従業員列 +
 * 日付方向の横スクロール) に統一している。仕様は「≥1280 フルグリッド / 768-1279 sticky列固定」の
 * 2段だが、sticky 列固定は幅を問わず無害でありむしろ月次サイクル(31列)では1280pxでも横スクロールが
 * 必要になる (§6-0 原則3: 列数を16列固定にしない、§4-11c N-3)。768px を境に「グリッド/日付ドリル型」
 * を切り替える1段に単純化した (D-204、レビューで指摘があれば3段へ分離する)。
 */

type BoardAssignment = PersonRef & {
  assignment_id: string;
  person_name: string | null;
  work_date: string;
  start_at: string;
  end_at: string;
  break_minutes: number;
  planned_labor_minutes: number;
  home_node_id: string;
  work_node_id: string;
  shift_pattern_id: string | null;
  note: string | null;
  origin: 'standing_pattern' | 'manual' | 'preference_based' | 'auto_generated';
  status: 'draft' | 'store_confirmed' | 'confirmed' | 'cancelled';
  is_locked: boolean;
  row_version: number;
};

type BoardPreference = PersonRef & {
  preference_id: string;
  person_name: string | null;
  target_date: string;
  start_time: string | null;
  end_time: string | null;
  preference_level_id: string;
};

type BoardViolation = {
  violation_id: string;
  work_date: string | null;
  violation_kind: string;
  message_key: string;
  subject_employee_id: string | null;
  subject_external_person_id: string | null;
  subject_name: string | null;
  detail: Record<string, unknown> | null;
  acknowledged_by_user_id: string | null;
  acknowledged_at: string | null;
  acknowledge_reason: string | null;
};

type BoardResponse = {
  period: { period_id: string; status: string; start_date: string; end_date: string };
  assignments: BoardAssignment[];
  preferences: BoardPreference[];
  violations: BoardViolation[];
  labor_cost: {
    granularity: 'monthly';
    estimate: { labor_minutes: number; amount: number } | null;
    // v0.4 追加要件⑮ (D-573): 3値化 (予定/実績/見込み)
    actual: { labor_minutes: number; amount: number } | null;
    provisional: { labor_minutes: number; amount: number } | null;
  } | null;
  // v0.4 TD-25 (D-261/D-262、§17-4-4): 個人ごとの週所定労働日数の目標値 (個人上書き→ノード既定→未設定)。
  contracted_work_days: Record<string, { value: number; source_kind: string; source_node_id: string | null }>;
};

type PeriodNode = { node_id: string; node_name: string; draft_status: string };
type PeriodNodesResponse = { period: { period_id: string; label: string; status: string }; nodes: PeriodNode[] };

type RosterPerson = { employee_id: string | null; external_person_id: string | null; display_name: string };
// v0.4 追加要件⑩ (D-325/D-326): 応援可能だが通常は編成対象外の候補 (個別指定 + 階層既定の和集合)。
type SupportCandidate = { employee_id: string; display_name: string; home_node_id: string | null };
type RosterResponse = { node_id: string; people: RosterPerson[]; support_candidates: SupportCandidate[] };

type DraftRun = {
  run_id: string; period_id: string; node_id: string; status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  mode: 'partial' | 'full';
  stats: {
    assignments_created?: number; skipped_double_booking?: number;
    // v0.4 追加要件⑬ (D-406/D-409): 貪欲法が打ち切った時点で未充足のまま残った制約 (不足理由の可視化)。
    unmet_constraints?: Array<{ work_date: string; group_id: string; constraint_id: string }>;
  } | null;
  error: string | null;
  started_at: string | null; finished_at: string | null;
};

const ORIGIN_META: Record<BoardAssignment['origin'], { label: string; icon: typeof PenLine; badgeClass: string }> = {
  // ⚠️ D-55 必須要件④: manual を警告色にしない (色だけで区別しない、§4-11b CRITICAL C-2)
  // ⚠️ design-expert H-3: ラベルは pages/ShiftMyPage.tsx (F-1) の既存語彙に合わせる (F-6 で
  // lib/shift-format.ts の ORIGIN_LABELS へ共通化、§8-55対策)。'下書き' は shift_assignments.status
  // の意味 (未確定) と衝突し、confirmed 済みの自動生成枠が永久に「下書き」表示になってしまう。
  standing_pattern: { label: ORIGIN_LABELS.standing_pattern!, icon: CalendarCheck2, badgeClass: 'bg-surface-sunken text-text-body border-surface-border' },
  manual: { label: ORIGIN_LABELS.manual!, icon: PenLine, badgeClass: 'bg-misespark-navy/10 text-misespark-navy border-misespark-navy/20' },
  preference_based: { label: ORIGIN_LABELS.preference_based!, icon: Heart, badgeClass: 'bg-surface-sunken text-text-body border-surface-border' },
  // ⚠️ design-expert H-2: misespark-orange は alert.border と同一hex (#FE6A03) で警告色と衝突するため、
  // 「シフト」機能の既存ブランドカラー (group.shift、紫) に置き換える (§4-11b「色だけで区別しない」の
  // 徹底: アイコン+文言に加え、警告色ファミリーとの衝突自体も避ける)
  auto_generated: { label: ORIGIN_LABELS.auto_generated!, icon: Sparkles, badgeClass: 'bg-group-shift-bg text-group-shift border-group-shift/30' },
};

const formatLaborMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}時間` : `${h}時間${m}分`;
};

// ⚠️ NF-11 (人・組織機能 増分④、§8-152 と同型の機械検証): この辞書は1行1キーで記述すること。
// web-static-dictionaries.test.ts の抽出ロジックが「改行直後のキー」のみを拾うため、
// 1行に複数キーを並べると検証から漏れて登録漏れが検出されなくなる。
const VIOLATION_KIND_LABELS: Record<string, string> = {
  staffing_shortage: '人員不足',
  staffing_excess: '人員過剰',
  overtime_limit: '法定労働時間超過',
  agreement_36_missing: '36協定未締結',
  agreement_36_expired: '36協定失効',
  income_cap: '年収の壁',
  business_hours: '営業時間外',
  preference_conflict: '希望との不一致',
  double_booking: '二重登録',
  rest_interval: '休息時間不足',
  statutory_holiday_missing: '法定休日不足',
  contracted_days_shortfall: '所定労働日数不足',
  leave_conflict: '休暇との重複',
  five_day_obligation_risk: '年5日義務の未達リスク',
  // NF-11 機械検証の導入時に判明した既存の欠落 (増分④のスコープではない、§4-3 の指示どおり別件として補完)
  travel_time_insufficient: '移動時間不足',
  // 人・組織機能 増分④ (J-13e、§4-3): 法令ガード
  minor_night_work: '年少者の深夜業',
  minor_overtime: '年少者の時間外労働',
  minor_holiday_work: '年少者の休日労働',
  exemption_violation: '免除請求への抵触',
  legal_guard_undeterminable: '法令ガード判定不能 (生年月日未登録)',
};

const toDateOnly = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (yyyyMmDd: string, days: number): string => {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d, 0, 0, 0, 0);
  dt.setDate(dt.getDate() + days);
  return toDateOnly(dt);
};
const buildDateList = (from: string, to: string): string[] => {
  const out: string[] = [];
  for (let cur = from; cur <= to; cur = addDays(cur, 1)) out.push(cur);
  return out;
};

function OriginBadge({ origin }: { origin: BoardAssignment['origin'] }) {
  const meta = ORIGIN_META[origin] ?? ORIGIN_META.manual;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${meta.badgeClass}`}>
      <Icon size={11} aria-hidden />
      {meta.label}
    </span>
  );
}

// v0.4 TD-25 (D-261/D-262、§17-4-4): 個人ごとの充足バッジ。design-expert Critical指摘により
// `title` 属性 (スクリーンリーダー未読上げ、ui-guidelines.md §2.4で禁止) を廃止し、
// InheritanceSourceBadge.tsx と同じ「aria-describedby + sr-only + クリック開閉」パターンに統一する。
function ContractedDaysBadge({ workedDays, target, sourceKind }: { workedDays: number; target: number; sourceKind: string }) {
  const [open, setOpen] = useState(false);
  const descId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const short = workedDays < target;
  // 契約区分マスタの残業計算連携 (migration 0192、§13-42): design-and-codeレビュー指摘MEDIUM是正
  const sourceLabel = sourceKind === 'person' ? '個別の契約' : sourceKind === 'contract_class' ? '契約区分マスタ' : '組織の標準値';

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block mt-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-describedby={descId}
        aria-expanded={open}
        className={`inline-flex items-center min-h-[20px] text-xs px-1 py-0.5 rounded border whitespace-nowrap ${short ? 'bg-alert-bg text-alert-text border-alert-border' : 'bg-surface-sunken text-text-sub border-surface-border'}`}
      >
        所定 {workedDays}/{target}日
      </button>
      <span id={descId} className="sr-only">{`週所定労働日数 (${sourceLabel}): ${target}日`}</span>
      {open && (
        <div
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-max max-w-[200px] rounded border border-surface-border bg-white shadow-lg px-3 py-2 text-xs text-text-body break-words"
        >
          週所定労働日数 ({sourceLabel}): {target}日
        </div>
      )}
    </div>
  );
}

function OriginLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-text-sub" aria-hidden={false}>
      <span className="font-semibold">凡例:</span>
      {(Object.keys(ORIGIN_META) as BoardAssignment['origin'][]).map((k) => (
        <OriginBadge key={k} origin={k} />
      ))}
    </div>
  );
}

// v0.4 追加要件⑪ 章② (D-391): 由来 (ORiginLegend) とは別チャネルの凡例。両方を同時に見たいケース
// (例: 深夜帯の枠が manual で上書きされている=要確認) があるため排他トグルにせず併置する。
function TimeBandLegend({ bands }: { bands: TimeBand[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-text-sub">
      {bands.map((b) => {
        const Icon = b.icon_key ? TIME_BAND_ICON_COMPONENTS[b.icon_key] : null;
        const colorCls = b.color_token ? TIME_BAND_COLOR_CLASSES[b.color_token] : null;
        return (
          <span key={b.band_id} className="inline-flex items-center gap-1">
            <span aria-hidden className={`w-2 h-2 rounded-sm shrink-0 ${colorCls ?? 'bg-surface-border'}`} />
            {Icon && <Icon size={10} aria-hidden />}
            {b.label}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 各枠の根拠 (D-55 必須要件②)
// ---------------------------------------------------------------------------
type DraftReason = { reason_id: string; run_id: string; reason_kind: string; detail: Record<string, unknown> };
const REASON_KIND_LABELS: Record<string, string> = {
  standing_pattern: '通常パターンからの展開', preference_match: '希望に基づく割当', skill_coverage: '必要な人材像の充足',
  priority_score: '優先度スコアによる選定', fallback: 'フォールバック割当', locked: '固定枠 (再生成の対象外)',
};

function ReasonsModal({ assignmentId, onClose }: { assignmentId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<{ reasons: DraftReason[] }>({
    queryKey: ['shift-assignment-reasons', assignmentId],
    queryFn: () => apiFetch(`/shift/assignments/${assignmentId}/reasons`),
  });
  const reasons = data?.reasons ?? [];
  return (
    <Modal open title="この枠の根拠" onClose={onClose} size="sm" footer={<Button variant="secondary" onClick={onClose}>閉じる</Button>}>
      {isLoading ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : reasons.length === 0 ? (
        <p className="text-sm text-text-sub">この枠は手動で作成されたか、下書き生成の根拠が記録されていません。</p>
      ) : (
        <ul className="space-y-3">
          {reasons.map((r) => (
            <li key={r.reason_id} className="text-sm">
              <div className="font-semibold text-text-body">{REASON_KIND_LABELS[r.reason_kind] ?? r.reason_kind}</div>
              <ul className="mt-1 space-y-0.5 text-xs text-text-sub">
                {Object.entries(r.detail).filter(([k]) => k !== 'stage_id').map(([k, v]) => (
                  <li key={k}>{k}: {String(v)}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 割当の追加・編集モーダル
// ---------------------------------------------------------------------------
type EditorTarget =
  // ⚠️ design-and-code MEDIUM指摘: home_node_id を閲覧中の店舗に決め打ちしない (応援で来ている
  // 他店所属者の場合、その人の実際の home_node_id を使う)
  | { mode: 'create'; person: RosterPerson; workDate: string; homeNodeId: string }
  | { mode: 'edit'; assignment: BoardAssignment };

function AssignmentEditorModal({
  target, nodeId, periodId, onClose, onSaved, onConflict, canCreate, canUpdate, canDelete,
}: {
  target: EditorTarget; nodeId: string; periodId: string; onClose: () => void; onSaved: () => void;
  canCreate: boolean; canUpdate: boolean; canDelete: boolean;
  /** ⚠️ QA指摘 M-2: サーバ側の状態が変わっていた (楽観ロック不一致・取消済み・締め済み) 場合に
   *  呼ばれる。モーダルは古い row_version を握ったまま再送しても再び失敗するだけのため、
   *  onSaved と同じくボードを再取得しモーダルを閉じる (再度開けば最新状態から編集できる)。 */
  onConflict: () => void;
}) {
  const isEdit = target.mode === 'edit';
  const workDate = isEdit ? target.assignment.work_date : target.workDate;
  const personLabel = isEdit ? (target.assignment.person_name ?? '(氏名不明)') : target.person.display_name;
  const holidays = useHolidays(workDate, workDate, nodeId);

  const [time, setTime] = useState<TimeRangeInputValue>(() => (isEdit
    ? { startTime: isoToLocalHm(target.assignment.start_at), endTime: isoToLocalHm(target.assignment.end_at) }
    : { startTime: '09:00', endTime: '17:00' }));
  const [breakMinutes, setBreakMinutes] = useState(isEdit ? target.assignment.break_minutes : 0);
  const [note, setNote] = useState(isEdit ? (target.assignment.note ?? '') : '');
  const [isLocked, setIsLocked] = useState(isEdit ? target.assignment.is_locked : false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // v0.4 追加7要件⑤ (D-235): パターンから選ぶと当該ノードの実効時刻を TimeRangeInput へ流し込む
  // (TimeRangeInput の presets 機構、§4-11b で先行実装済みのものを利用)。Q-179 の既定案どおり、
  // 選択後に手修正しても shift_pattern_id は保持し「由来」として残す。
  const [shiftPatternId, setShiftPatternId] = useState<string | null>(isEdit ? target.assignment.shift_pattern_id : null);
  const inputMode = usePreferenceInputMode(nodeId);
  const { presets } = useShiftPatternPresets(nodeId, inputMode !== 'time_range');
  // design-and-code 指摘 (Q-179): 由来を保持するだけでなく「逸脱している旨をボードに表示」まで
  // 設計の確定事項。パターンの現在の実効時刻と、この割当が実際に持つ時刻を突き合わせて判定する。
  const selectedPreset = shiftPatternId ? presets.find((p) => p.id === shiftPatternId) : undefined;
  const deviatesFromPattern = !!selectedPreset
    && (selectedPreset.startTime !== time.startTime || selectedPreset.endTime !== time.endTime);

  // ⚠️ QA指摘 M-2: row_version 不一致・取消済み・期間締め済みは「このモーダルの前提自体が古い」
  // ケースのためボードを再取得しモーダルを閉じる。二重ブッキング (DOUBLE_BOOKING) は入力側の
  // 時刻を直せば解決しうるため、ここには含めずモーダルを開いたままにする。
  const STALE_STATE_CODES = new Set(['ROW_VERSION_MISMATCH', 'ASSIGNMENT_CANCELLED', 'PERIOD_CLOSED']);
  const handleMutationError = (e: unknown, fallback: string) => {
    if (e instanceof ApiClientError) {
      toast.error(e.message);
      if (STALE_STATE_CODES.has(e.code)) { onConflict(); return; }
    } else {
      toast.error(fallback);
    }
  };

  const buildStartEnd = (): { startAt: string; endAt: string } | null => {
    if (!time.startTime || !time.endTime) return null;
    const startAt = buildLocalIso(workDate, time.startTime);
    const crossesMidnight = time.endTime <= time.startTime;
    const endAt = buildLocalIso(crossesMidnight ? addDays(workDate, 1) : workDate, time.endTime);
    return { startAt, endAt };
  };

  const createMut = useMutation({
    mutationFn: () => {
      const range = buildStartEnd();
      if (!range || target.mode !== 'create') throw new Error('時刻を指定してください');
      return apiFetch('/shift/assignments', {
        method: 'POST',
        body: JSON.stringify({
          period_id: periodId,
          employee_id: target.person.employee_id, external_person_id: target.person.external_person_id,
          start_at: range.startAt, end_at: range.endAt, break_minutes: breakMinutes,
          home_node_id: target.homeNodeId, work_node_id: nodeId, origin: 'manual',
          shift_pattern_id: shiftPatternId, note: note.trim() || null,
        }),
      });
    },
    onSuccess: () => { toast.success('シフトを追加しました'); onSaved(); },
    onError: (e) => handleMutationError(e, '追加に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: () => {
      const range = buildStartEnd();
      if (!range || target.mode !== 'edit') throw new Error('時刻を指定してください');
      return apiFetch<{ status?: string }>(`/shift/assignments/${target.assignment.assignment_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          row_version: target.assignment.row_version,
          start_at: range.startAt, end_at: range.endAt, break_minutes: breakMinutes,
          is_locked: isLocked, shift_pattern_id: shiftPatternId, note: note.trim() || null,
        }),
      });
    },
    onSuccess: (res: { status?: string }) => {
      toast.success(res?.status === 'pending_approval' ? '変更を承認申請しました' : 'シフトを更新しました');
      onSaved();
    },
    onError: (e) => handleMutationError(e, '更新に失敗しました'),
  });

  const cancelMut = useMutation({
    mutationFn: () => {
      if (target.mode !== 'edit') throw new Error('unreachable');
      return apiFetch<{ status?: string }>(`/shift/assignments/${target.assignment.assignment_id}?row_version=${target.assignment.row_version}`, { method: 'DELETE' });
    },
    onSuccess: (res: { status?: string }) => {
      toast.success(res?.status === 'pending_approval' ? '取消を承認申請しました' : 'シフトを取り消しました');
      onSaved();
    },
    onError: (e) => handleMutationError(e, '取消に失敗しました'),
  });

  const saving = createMut.isPending || updateMut.isPending || cancelMut.isPending;
  const canSave = !!time.startTime && !!time.endTime;

  return (
    <Modal
      open
      title={isEdit ? 'シフトを編集' : 'シフトを追加'}
      onClose={onClose}
      size="sm"
      footer={(
        <>
          {/* design-and-code MEDIUM指摘: create/update/delete をそれぞれ別 permission で判定する
              (own_store の permission 行がアクションごとに個別付与されうるカスタムロールを考慮)。
              design-expert M-1: 取り消すボタンはここでは確認ダイアログを開くだけで実際には何も
              破壊しないため secondary にする (実際の破壊操作の赤は下の確認ダイアログ側の1箇所に絞る) */}
          {isEdit && canDelete && (
            <Button variant="secondary" onClick={() => setConfirmCancel(true)} disabled={saving}>取り消す</Button>
          )}
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>キャンセル</Button>
          {(isEdit ? canUpdate : canCreate) && (
            <Button
              variant="primary" disabled={!canSave} loading={createMut.isPending || updateMut.isPending}
              onClick={() => (isEdit ? updateMut.mutate() : createMut.mutate())}
            >
              {isEdit ? '保存する' : '追加する'}
            </Button>
          )}
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <div className="text-sm font-semibold text-text-body">{personLabel}</div>
          <div className="text-xs text-text-sub"><DateLabel date={workDate} withWeekday holidays={holidays} /></div>
        </div>
        <TimeRangeInput
          value={time} onChange={setTime} legend="勤務時間"
          presets={presets} selectedPresetId={shiftPatternId} onPresetSelect={setShiftPatternId}
          hideManualInput={inputMode === 'pattern'}
        />
        {deviatesFromPattern && (
          <p className="text-xs text-alert-text px-1.5 py-1 rounded bg-alert-bg">
            「{selectedPreset!.label}」パターン ({selectedPreset!.startTime}〜{selectedPreset!.endTime}) から時刻を変更しています。
          </p>
        )}
        <div>
          <label className="block text-sm text-text-sub mb-1">休憩(分)</label>
          <input
            type="number" min={0} value={breakMinutes}
            onChange={(e) => setBreakMinutes(Math.max(0, Number(e.target.value) || 0))}
            className="w-28 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={isLocked} onChange={(e) => setIsLocked(e.target.checked)} className="h-4 w-4" />
            この枠を固定する (下書き再生成の対象外にする)
          </label>
        )}
        <div>
          <label className="block text-sm text-text-sub mb-1">メモ (任意)</label>
          <input
            value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
          />
        </div>
      </div>

      {confirmCancel && (
        <Modal
          open title="このシフトを取り消しますか？" onClose={() => setConfirmCancel(false)} size="sm" zIndexClass="z-[60]"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setConfirmCancel(false)}>戻る</Button>
              <Button variant="danger" loading={cancelMut.isPending} onClick={() => cancelMut.mutate()}>取り消す</Button>
            </>
          )}
        >
          <p className="text-sm text-text-body">取り消した記録は履歴に残ります。元に戻すには改めてシフトを追加してください。</p>
        </Modal>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 違反パネル (D-55 必須要件③)
// ---------------------------------------------------------------------------
function ViolationsPanel({
  violations, canAcknowledge, onAcknowledge,
}: {
  violations: BoardViolation[]; canAcknowledge: boolean; onAcknowledge: (id: string, reason: string) => void;
}) {
  const [target, setTarget] = useState<BoardViolation | null>(null);
  const [reason, setReason] = useState('');
  // design-expert M-6: 日付ヘッダーの警告アイコンと突き合わせやすいよう work_date 順に並べる
  // (null=店舗全体の警告は末尾に集める)
  const active = violations.filter((v) => !v.acknowledged_at)
    .sort((a, b) => (a.work_date ?? '￿').localeCompare(b.work_date ?? '￿'));

  if (violations.length === 0) {
    return <p className="text-sm text-text-sub">現在、警告はありません。</p>;
  }

  return (
    <div className="space-y-2">
      {active.length === 0 && <p className="text-xs text-text-sub">未対応の警告はありません (対応済み {violations.length} 件)。</p>}
      {active.map((v) => (
        <div key={v.violation_id} className="p-2.5 border border-alert-border bg-alert-bg rounded-lg text-xs">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold text-alert-text flex items-center gap-1">
                <AlertTriangle size={13} aria-hidden />
                {VIOLATION_KIND_LABELS[v.violation_kind] ?? v.violation_kind}
              </div>
              <div className="text-alert-text/90 mt-0.5">
                {v.work_date && <span>{formatDateLabel(v.work_date)} ・ </span>}
                {v.subject_name && <span>{v.subject_name} ・ </span>}
                <ViolationDetail detail={v.detail} kind={v.violation_kind} />
              </div>
            </div>
            {canAcknowledge && (
              <Button variant="secondary" size="sm" onClick={() => setTarget(v)}>対応済みにする</Button>
            )}
          </div>
        </div>
      ))}

      <Modal
        open={!!target} title="この警告を対応済みにしますか？" onClose={() => { setTarget(null); setReason(''); }} size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => { setTarget(null); setReason(''); }}>キャンセル</Button>
            <Button
              variant="primary"
              onClick={() => { if (target) onAcknowledge(target.violation_id, reason); setTarget(null); setReason(''); }}
            >
              対応済みにする
            </Button>
          </>
        )}
      >
        <div className="space-y-2 text-sm">
          <p className="text-text-body">警告を解消せずこのまま進める場合、その事実が記録に残ります (理由の入力は任意です)。</p>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
            placeholder="理由 (任意)"
            className="w-full px-3 py-2 rounded border border-surface-border focus-ring"
          />
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 下書き生成 (章⑥、D-55 実装要件・二重セーフティネット)
// ---------------------------------------------------------------------------
const DRAFT_POLL_INTERVAL_MS = 3000;
// ⚠️ サーバ側の Queue consumer は §8-40 の CPU 制約を踏まえ店舗単位に分割済みだが、v0.3 の
// AI メニュー開発で「15分ハングし復帰不能」の実害があったため (§4-11 表参照)、クライアント側にも
// 独立した安全網を置く。lib/sse.ts の READ_IDLE_TIMEOUT_MS (220秒) と同じ発想で、ポーリングでの
// 「未完了のまま許容する時間」の上限を設ける。
const DRAFT_CLIENT_TIMEOUT_MS = 5 * 60_000;

function DraftGenerateControl({ periodId, nodeId, onCompleted }: { periodId: string; nodeId: string; onCompleted: () => void }) {
  const [runId, setRunId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    if (!runId) return undefined;
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [runId]);

  const elapsedMs = startedAt ? nowTick - startedAt : 0;
  const clientTimedOut = !!runId && elapsedMs > DRAFT_CLIENT_TIMEOUT_MS;

  const runQuery = useQuery<DraftRun>({
    queryKey: ['shift-draft-run', runId],
    queryFn: () => apiFetch(`/shift/draft-runs/${runId}`),
    enabled: !!runId && !clientTimedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? DRAFT_POLL_INTERVAL_MS : false;
    },
  });

  useEffect(() => {
    if (runQuery.data?.status === 'succeeded') {
      const created = runQuery.data.stats?.assignments_created ?? 0;
      toast.success(`下書きを生成しました (${created}件の枠を作成)`);
      // v0.4 追加要件⑬ (D-406/D-409、正典 §3-5 H-7): 貪欲法が打ち切った時点で埋められなかった枠を
      // 案内する。詳細は既存の日付ヘッダーの「不足」バッジ・違反パネル (staffing_shortage) から確認できる
      // (recomputeSoftViolationsForNodeDate が生成直後に同じ制約の充足状況を再計算して記録している)。
      const unmetDates = new Set((runQuery.data.stats?.unmet_constraints ?? []).map((u) => u.work_date));
      if (unmetDates.size > 0) {
        toast.error(`${unmetDates.size}日分の枠を必要人員どおりに埋めきれませんでした。日付ヘッダーの「不足」表示から確認してください。`);
      }
      onCompleted();
      setRunId(null); setStartedAt(null);
    } else if (runQuery.data?.status === 'failed') {
      toast.error(runQuery.data.error ?? '下書き生成に失敗しました');
      setRunId(null); setStartedAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCompleted は毎レンダー新規参照になるため依存から除外
  }, [runQuery.data?.status]);

  const startMut = useMutation({
    mutationFn: () => apiFetch<{ run_id: string }>(`/shift/periods/${periodId}/nodes/${nodeId}/draft`, {
      method: 'POST', body: JSON.stringify({ mode: 'partial' }),
    }),
    onSuccess: (res) => { setRunId(res.run_id); setStartedAt(Date.now()); setNowTick(Date.now()); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '下書き生成の開始に失敗しました'),
  });

  const running = !!runId && (runQuery.data?.status === 'queued' || runQuery.data?.status === 'running' || !runQuery.data);

  if (clientTimedOut) {
    return (
      <div className="flex items-center gap-2 text-xs text-alert-text">
        <span>生成に時間がかかっています。しばらくしてから状態を確認してください。</span>
        <Button variant="secondary" size="sm" onClick={() => { setStartedAt(Date.now()); setNowTick(Date.now()); }}>もう少し待つ</Button>
        <Button variant="ghost" size="sm" onClick={() => { setRunId(null); setStartedAt(null); }}>中止扱いにする</Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button variant="secondary" size="sm" leftIcon={<Sparkles size={14} />} loading={startMut.isPending || running} onClick={() => startMut.mutate()}>
        下書きを生成
      </Button>
      {running && (
        <span className="text-xs text-text-sub tabular-nums">
          生成中です (経過{formatElapsedDuration(Math.floor(elapsedMs / 1000))}・他の画面に移動しても生成は続きます)
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// メイン
// ---------------------------------------------------------------------------
export function ShiftBoardPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const { id: periodId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const nodeId = searchParams.get('node_id') ?? '';
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [reasonsAssignmentId, setReasonsAssignmentId] = useState<string | null>(null);

  // ⚠️ design-and-code MEDIUM指摘: 'update' だけで「追加」「下書き生成」まで一括りにすると、
  // バックエンドは POST /shift/assignments・POST .../draft を 'create' scope で判定しているため
  // (own_store の permission 行が create/update/delete で別々に付与されうるカスタムロールで)
  // 表示と実際の許可がずれる。3アクションを個別に判定する。
  const canCreate = hasPermission(user?.effective_permissions, 'shift_periods', 'create');
  const canUpdate = hasPermission(user?.effective_permissions, 'shift_periods', 'update');
  const canDelete = hasPermission(user?.effective_permissions, 'shift_periods', 'delete');
  // ⚠️ TD-22: これは全社調整画面への導線判定であり「全店舗スコープ保有」ではない。
  // D-351 (migration 0107) で GET /shift/periods/:id/coordination のゲートが
  // 「scope 不問 + 保有スコープで絞り込み」へ緩和されたため、ここを hasTenantWidePermission に
  // 変えると node_scope のエリアマネージャーが全社調整に到達できなくなる機能退行になる。
  const canOpenCoordination = hasPermission(user?.effective_permissions, 'shift_periods', 'manage');

  const { data: nodesData } = useQuery<PeriodNodesResponse>({
    queryKey: ['shift-period-nodes-board', periodId],
    queryFn: () => apiFetch(`/shift/periods/${periodId}/nodes`),
    enabled: !!periodId,
  });
  const nodes = nodesData?.nodes ?? [];
  useEffect(() => {
    if (!nodeId && nodes.length > 0) setSearchParams({ node_id: nodes[0]!.node_id }, { replace: true });
  }, [nodeId, nodes, setSearchParams]);

  const boardQuery = useQuery<BoardResponse>({
    queryKey: ['shift-board', periodId, nodeId],
    queryFn: () => apiFetch(`/shift/periods/${periodId}/nodes/${nodeId}/board`),
    enabled: !!periodId && !!nodeId,
  });
  const board = boardQuery.data;

  // ⚠️ shift_preference:view が無いロールではロースター取得が403になりうるため、失敗しても
  // ボード全体は壊さず空ロースターへ縮退させる (氏名解決の主経路は board.assignments 側にある)。
  const rosterQuery = useQuery<RosterResponse>({
    queryKey: ['shift-board-roster', nodeId],
    queryFn: () => apiFetch(`/standing-patterns?node_id=${nodeId}`),
    enabled: !!nodeId,
    retry: false,
  });
  const roster = rosterQuery.data?.people ?? [];
  const supportCandidates = rosterQuery.data?.support_candidates ?? [];
  // v0.4 追加要件⑩ (D-325/D-326): 「応援候補」から店長が選んでボードに一時的に行を追加する
  // (実際の割当が1件も無ければ再読込で消える、DBへは何も書き込まれない純粋な表示上の操作)。
  const [addedSupportEmployeeIds, setAddedSupportEmployeeIds] = useState<Set<string>>(new Set());
  useEffect(() => { setAddedSupportEmployeeIds(new Set()); }, [nodeId]);

  // v0.4 追加要件⑪ 章② (D-354): 時間帯区分 (テナント全体1セット、参照専用エンドポイント)。
  const timeBandsQuery = useQuery<{ time_bands: TimeBand[] }>({
    queryKey: ['shift-time-bands-reference'],
    queryFn: () => apiFetch('/shift-report/time-bands'),
  });
  const timeBands = timeBandsQuery.data?.time_bands ?? [];
  // ⚠️ D-391: 由来 (常時表示) とは別チャネルの非排他トグル。per-viewer で永続化する (時間帯バーの
  // 表示トグルのみ localStorage に残す、絞り込みフィルタとは異なる扱い)。
  const [showTimeBands, setShowTimeBands] = useState(() => localStorage.getItem('shift-board-show-time-bands') !== '0');
  // v0.4 追加要件⑮ (D-573): 人件費見込み表示の確度 (予定/実績/見込み)。従来の自動フォールバック
  // (provisional ?? estimate) の既定と同じ「見込み」を初期値にする。
  const [boardLaborCostConfidence, setBoardLaborCostConfidence] = useState<LaborCostConfidence>('provisional');
  useEffect(() => { localStorage.setItem('shift-board-show-time-bands', showTimeBands ? '1' : '0'); }, [showTimeBands]);

  const invalidateBoard = () => {
    qc.invalidateQueries({ queryKey: ['shift-board', periodId, nodeId] });
    qc.invalidateQueries({ queryKey: ['shift-period-nodes-board', periodId] });
  };

  const acknowledgeMut = useMutation({
    mutationFn: ({ violationId, reason }: { violationId: string; reason: string }) => apiFetch(`/shift/violations/${violationId}/acknowledge`, {
      method: 'POST', body: JSON.stringify({ reason: reason.trim() || null }),
    }),
    onSuccess: () => { toast.success('対応済みにしました'); invalidateBoard(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '処理に失敗しました'),
  });

  const submitMut = useMutation({
    mutationFn: () => apiFetch(`/shift/periods/${periodId}/nodes/${nodeId}/submit`, { method: 'POST' }),
    onSuccess: () => { toast.success('店舗案を確定しました'); invalidateBoard(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  // 従業員/外部人材の行一覧: ロースター (店舗所属者) + ボードに現れる人物 (応援等でロースターに
  // 含まれない場合の取りこぼし防止) を name をキーに統合する
  const rows = useMemo(() => {
    // ⚠️ design-and-code MEDIUM指摘: ロースター (店舗所属者) の home は当該店舗そのものだが、
    // ロースターに無く割当だけで見えている人物 (応援で来ている他店所属者) はその人の実際の
    // home_node_id を保持しないと、「+」から新規追加した際に home_node_id が常に閲覧中の店舗に
    // 上書きされ D-14 の人件費計上先の按分が壊れる。
    const map = new Map<string, { key: string; ref: PersonRef; displayName: string; homeNodeId: string }>();
    for (const p of roster) {
      const ref = { employee_id: p.employee_id, external_person_id: p.external_person_id };
      map.set(personKey(ref), { key: personKey(ref), ref, displayName: p.display_name, homeNodeId: nodeId });
    }
    for (const a of board?.assignments ?? []) {
      const key = personKey(a);
      if (!map.has(key)) map.set(key, { key, ref: a, displayName: a.person_name ?? '(氏名不明)', homeNodeId: a.home_node_id });
    }
    // v0.4 追加要件⑩: 店長が「応援候補」から追加した人物。home_node_id は本来の主所属店舗を保つ
    // (この店舗に固定すると D-14 の人件費按分先が壊れる、ロースター行との違いはここのみ)。
    for (const employeeId of addedSupportEmployeeIds) {
      const candidate = supportCandidates.find((sc) => sc.employee_id === employeeId);
      if (!candidate) continue;
      const ref = { employee_id: candidate.employee_id, external_person_id: null };
      const key = personKey(ref);
      if (!map.has(key)) {
        map.set(key, { key, ref, displayName: candidate.display_name, homeNodeId: candidate.home_node_id ?? nodeId });
      }
    }
    return [...map.values()].sort((x, y) => x.displayName.localeCompare(y.displayName, 'ja'));
  }, [roster, board, nodeId, addedSupportEmployeeIds, supportCandidates]);

  const dateList = useMemo(() => (board ? buildDateList(board.period.start_date, board.period.end_date) : []), [board]);
  const holidays = useHolidays(board?.period.start_date ?? '', board?.period.end_date ?? '', nodeId);

  const assignmentsByCell = useMemo(() => {
    const map = new Map<string, BoardAssignment[]>();
    for (const a of board?.assignments ?? []) {
      const key = `${personKey(a)}|${a.work_date}`;
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return map;
  }, [board]);

  // v0.4 TD-25 (D-261/D-262、§17-4-4): 個人ごとの「今期間の割当日数 / (週所定労働日数 × 期間の週数)」の
  // 充足率。⚠️ shift_violations とは独立の単純集計 (期間の途中経過は「まだ足りていないだけ」で違反ではない)。
  const contractedDaysFulfillment = useMemo(() => {
    const map = new Map<string, { workedDays: number; target: number; sourceKind: string }>();
    if (!board) return map;
    const periodDays = Math.round((Date.parse(`${board.period.end_date}T00:00:00Z`) - Date.parse(`${board.period.start_date}T00:00:00Z`)) / 86400000) + 1;
    for (const [employeeId, target] of Object.entries(board.contracted_work_days)) {
      const workedDays = new Set(
        board.assignments.filter((a) => a.employee_id === employeeId).map((a) => a.work_date),
      ).size;
      const proratedTarget = Math.max(1, Math.round((target.value * periodDays) / 7));
      map.set(`E:${employeeId}`, { workedDays, target: proratedTarget, sourceKind: target.source_kind });
    }
    return map;
  }, [board]);

  // v0.4 追加要件⑪ 章② (D-392): 表示絞り込みトグルが参照する行単位の集計 (合計勤務時間・確定済み
  // 有無・希望提出有無・未対応違反有無・出現する時間帯区分)。
  // ⚠️ test-runner指摘: ページ内 useMemo に留めず lib/shift-board-filters.ts の純関数へ抽出し単体テストで
  // 固定する (pages/__tests__/ImportsPage.test.ts の deriveCommitOutcome と同じ理由)。
  const rowStats = useMemo(
    () => buildRowStats(rows.map((r) => r.key), board?.assignments ?? [], board?.preferences ?? [], board?.violations ?? [], timeBands),
    [rows, board, timeBands],
  );

  // ⚠️ D-392: フィルタ状態は localStorage に永続化しない (次回開いたときに絞られたままにならないよう
  // にする、showTimeBands とは異なる扱い)。ノード切替時にもリセットする。
  const [boardFilter, setBoardFilter] = useState<BoardFilterState>(EMPTY_BOARD_FILTER);
  useEffect(() => { setBoardFilter(EMPTY_BOARD_FILTER); }, [nodeId]);
  const boardFilterActive = isBoardFilterActive(boardFilter);
  const filteredRows = useMemo(() => {
    if (!boardFilterActive) return rows;
    return rows.filter((row) => matchesBoardFilter(rowStats.get(row.key), boardFilter));
  }, [rows, rowStats, boardFilter, boardFilterActive]);

  const violationsByDate = useMemo(() => {
    const map = new Map<string, BoardViolation[]>();
    for (const v of board?.violations ?? []) {
      if (!v.work_date) continue;
      const list = map.get(v.work_date) ?? [];
      list.push(v);
      map.set(v.work_date, list);
    }
    return map;
  }, [board]);
  const nodelessViolations = (board?.violations ?? []).filter((v) => !v.work_date);

  const dayCoverage = (date: string): 'shortage' | 'excess' | 'other' | 'ok' => {
    const vs = (violationsByDate.get(date) ?? []).filter((v) => !v.acknowledged_at);
    if (vs.some((v) => v.violation_kind === 'staffing_shortage')) return 'shortage';
    if (vs.some((v) => v.violation_kind === 'staffing_excess')) return 'excess';
    if (vs.length > 0) return 'other';
    return 'ok';
  };

  const currentNode = nodes.find((n) => n.node_id === nodeId);
  const alreadySubmitted = !!currentNode && ['submitted', 'coordinated', 'confirmed'].includes(currentNode.draft_status);

  if (!periodId) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-h2">{t('entity.shift_assignments', 'シフト編成ボード')}</h2>
          {nodesData && <p className="text-sm text-text-sub mt-1">{nodesData.period.label}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canOpenCoordination && (
            <Link
              to={`/shift/periods/${periodId}/coordination`}
              className="inline-flex items-center gap-1 min-h-[44px] px-2 text-xs text-misespark-navy hover:underline"
            >
              <ArrowLeftRight size={14} aria-hidden />全社調整を開く
            </Link>
          )}
          {nodes.length > 0 && (
            <Select value={nodeId || undefined} onValueChange={(v) => setSearchParams({ node_id: v })}>
              <SelectTrigger className="w-full sm:w-64 min-h-[44px]"><SelectValue placeholder="店舗を選ぶ" /></SelectTrigger>
              <SelectContent>
                {nodes.map((n) => <SelectItem key={n.node_id} value={n.node_id}>{n.node_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {!nodeId ? (
        <EmptyState message="対象店舗がありません" description="この期間に閲覧できる店舗がありません。" />
      ) : boardQuery.isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : boardQuery.isError ? (
        <div className="p-6 border border-danger-border bg-danger-bg rounded-lg text-danger-text flex flex-wrap items-center justify-between gap-2">
          <span>編成ボードの取得に失敗しました。通信状況をご確認のうえ、もう一度お試しください。</span>
          <Button variant="secondary" size="sm" onClick={() => boardQuery.refetch()}>再試行</Button>
        </div>
      ) : board ? (
        <>
          {/* ⚠️ design-expert H-4: 期間が全社確定済み (finalized/closed) の場合、編成ボードの
              編集操作自体は引き続き可能に見えるが実際には他店の応援枠を動かせない等の制約がかかる
              (D-62)。編集を始める前に状態を可視化する。 */}
          {(board.period.status === 'finalized' || board.period.status === 'closed') && (
            <div className="p-3 border border-alert-border bg-alert-bg rounded-lg text-sm text-alert-text flex items-center gap-2">
              <AlertTriangle size={14} aria-hidden className="shrink-0" />
              この期間は既に{board.period.status === 'closed' ? '締め済み' : '全社確定済み'}です。従業員へ通知済みのため、変更には制約がかかる場合があります。
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border border-surface-border rounded-lg bg-white">
            <div className="flex flex-wrap items-center gap-3">
              <OriginLegend />
              {timeBands.length > 0 && (
                <button
                  type="button"
                  aria-pressed={showTimeBands}
                  onClick={() => setShowTimeBands((v) => !v)}
                  className={`inline-flex items-center gap-1 min-h-[44px] px-2 rounded-full text-[11px] border ${
                    showTimeBands ? 'border-misespark-navy text-misespark-navy bg-misespark-navy/5' : 'border-surface-border text-text-sub'
                  }`}
                >
                  {showTimeBands ? <Eye size={11} aria-hidden /> : <EyeOff size={11} aria-hidden />}
                  時間帯を表示
                </button>
              )}
              {showTimeBands && timeBands.length > 0 && <TimeBandLegend bands={timeBands} />}
              {board.labor_cost && (
                <div className="flex flex-wrap items-center gap-2">
                  <LaborCostConfidenceToggle
                    value={boardLaborCostConfidence} onChange={setBoardLaborCostConfidence} ariaLabel="人件費の確度"
                  />
                  <span className="text-xs text-text-sub">
                    人件費{LABOR_COST_CONFIDENCE_LABELS[boardLaborCostConfidence]}: {formatCurrency(board.labor_cost[boardLaborCostConfidence]?.amount ?? null)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canCreate && !alreadySubmitted && <DraftGenerateControl periodId={periodId} nodeId={nodeId} onCompleted={invalidateBoard} />}
              {canUpdate && (
                alreadySubmitted ? (
                  <span className="inline-flex items-center gap-1 text-xs text-text-sub">
                    <CheckCircle2 size={13} aria-hidden className="text-misespark-navy" />
                    {DRAFT_STATUS_LABELS[currentNode!.draft_status] ?? '提出済み'}
                  </span>
                ) : (
                  <Button variant="primary" size="sm" loading={submitMut.isPending} onClick={() => submitMut.mutate()}>
                    この店舗の案を確定する
                  </Button>
                )
              )}
            </div>
          </div>

          {/* v0.4 追加要件⑩ (D-325/D-326): 応援候補 (個別指定 + 階層既定)。追加はこの画面上のみの
              一時的な表示操作で、DBへは何も書き込まない (実際に枠を作るのは各行の「+」)。 */}
          {canCreate && supportCandidates.some((sc) => !addedSupportEmployeeIds.has(sc.employee_id)) && (
            <div className="flex flex-wrap items-center gap-2 p-3 border border-surface-border rounded-lg bg-white">
              <span className="text-xs text-text-sub shrink-0">応援候補:</span>
              {supportCandidates.filter((sc) => !addedSupportEmployeeIds.has(sc.employee_id)).map((sc) => (
                <button
                  key={sc.employee_id}
                  type="button"
                  onClick={() => setAddedSupportEmployeeIds((prev) => new Set(prev).add(sc.employee_id))}
                  className="min-h-[44px] inline-flex items-center gap-1 rounded-full border border-dashed border-surface-border px-2.5 text-xs text-text-body hover:border-brand-red hover:text-brand-red"
                >
                  <Plus size={11} aria-hidden />{sc.display_name}
                </button>
              ))}
            </div>
          )}

          {/* v0.4 追加要件⑪ 章② (D-392): 表示絞り込み。編成ボードは編集画面のため、絞り込んだまま
              編成を確定すると非表示の従業員を見落とすリスクがある。件数チップ+1クリック解除を必須にし、
              フィルタ状態自体は localStorage に永続化しない (次回開いたときに絞られたままにならない)。 */}
          {rows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 border border-surface-border rounded-lg bg-white">
              <span className="text-xs text-text-sub shrink-0">表示絞り込み:</span>
              <button
                type="button" aria-pressed={boardFilter.confirmedOnly}
                onClick={() => setBoardFilter((f) => ({ ...f, confirmedOnly: !f.confirmedOnly }))}
                className={`min-h-[44px] px-2.5 rounded-full text-xs border ${
                  boardFilter.confirmedOnly ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body hover:border-misespark-navy'
                }`}
              >
                確定済みのみ
              </button>
              <button
                type="button" aria-pressed={boardFilter.unsubmittedOnly}
                onClick={() => setBoardFilter((f) => ({ ...f, unsubmittedOnly: !f.unsubmittedOnly }))}
                className={`min-h-[44px] px-2.5 rounded-full text-xs border ${
                  boardFilter.unsubmittedOnly ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body hover:border-misespark-navy'
                }`}
              >
                未提出者のみ
              </button>
              <button
                type="button" aria-pressed={boardFilter.violationsOnly}
                onClick={() => setBoardFilter((f) => ({ ...f, violationsOnly: !f.violationsOnly }))}
                className={`min-h-[44px] px-2.5 rounded-full text-xs border ${
                  boardFilter.violationsOnly ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body hover:border-misespark-navy'
                }`}
              >
                違反のある行のみ
              </button>
              {timeBands.length > 0 && (
                <Select
                  value={boardFilter.bandCode ?? '__all__'}
                  onValueChange={(v) => setBoardFilter((f) => ({ ...f, bandCode: v === '__all__' ? null : v }))}
                >
                  <SelectTrigger className="w-40 min-h-[44px] text-xs"><SelectValue placeholder="時間帯区分" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">すべての時間帯</SelectItem>
                    {timeBands.map((b) => <SelectItem key={b.band_id} value={b.code}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex-1" />
              {boardFilterActive && (
                <span className="inline-flex items-center gap-1.5 text-xs text-text-sub">
                  {rows.length}名中 {filteredRows.length}名を表示中
                  <button
                    type="button"
                    onClick={() => setBoardFilter(EMPTY_BOARD_FILTER)}
                    className="inline-flex items-center gap-0.5 min-h-[44px] px-1 text-misespark-navy hover:underline"
                  >
                    <X size={11} aria-hidden />解除
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <div className="border border-surface-border rounded-lg bg-white overflow-hidden">
              {/* ≥768px: グリッド表示 (sticky 従業員列 + 横スクロール) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="border-collapse w-full">
                  <thead>
                    <tr>
                      <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 140 }}>
                        <span>従業員</span>
                      </th>
                      {dateList.map((date) => {
                        const cov = dayCoverage(date);
                        return (
                          <th scope="col" key={date} className="border-b border-surface-border px-1.5 py-2 text-center text-xs text-text-sub" style={{ minWidth: 88 }}>
                            <div className="whitespace-nowrap"><DateLabel date={date} withWeekday holidays={holidays} /></div>
                            {cov === 'shortage' && <span className="inline-flex items-center gap-0.5 text-alert-text"><AlertTriangle size={10} aria-hidden />不足</span>}
                            {cov === 'excess' && <span className="inline-flex items-center gap-0.5 text-alert-text"><AlertTriangle size={10} aria-hidden />過剰</span>}
                            {cov === 'other' && <span className="inline-flex items-center gap-0.5 text-alert-text"><AlertTriangle size={10} aria-hidden />警告</span>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={dateList.length + 1} className="p-6 text-center text-sm text-text-sub">この店舗にはまだ従業員が割り当てられていません。</td></tr>
                    ) : filteredRows.length === 0 ? (
                      <tr><td colSpan={dateList.length + 1} className="p-6 text-center text-sm text-text-sub">絞り込み条件に一致する従業員がいません。</td></tr>
                    ) : filteredRows.map((row) => (
                      <tr key={row.key}>
                        <th
                          scope="row"
                          className="sticky left-0 z-10 bg-white border-b border-r border-surface-border px-3 py-2 text-left text-sm text-text-body font-normal"
                          aria-label={`${row.displayName}の週間シフト`}
                        >
                          <span className="truncate block max-w-[140px]">{row.displayName}</span>
                          {(() => {
                            const fulfillment = contractedDaysFulfillment.get(row.key);
                            if (!fulfillment) return null;
                            return <ContractedDaysBadge workedDays={fulfillment.workedDays} target={fulfillment.target} sourceKind={fulfillment.sourceKind} />;
                          })()}
                          {(() => {
                            const stat = rowStats.get(row.key);
                            if (!stat || stat.totalMinutes <= 0) return null;
                            return <div className="text-[10px] text-text-sub mt-0.5 tabular-nums">計 {formatLaborMinutes(stat.totalMinutes)}</div>;
                          })()}
                        </th>
                        {dateList.map((date) => {
                          const cellAssignments = assignmentsByCell.get(`${row.key}|${date}`) ?? [];
                          return (
                            <td key={date} className="border-b border-surface-border px-1 py-1.5 align-top">
                              <div className="flex flex-col gap-1 items-stretch">
                                {cellAssignments.map((a) => {
                                  const band = showTimeBands ? resolveTimeBand(isoToLocalHm(a.start_at), timeBands) : null;
                                  const BandIcon = band?.icon_key ? TIME_BAND_ICON_COMPONENTS[band.icon_key] : null;
                                  const bandColorCls = band?.color_token ? TIME_BAND_COLOR_CLASSES[band.color_token] : null;
                                  return (
                                    <div key={a.assignment_id} className="relative text-[11px] rounded border border-surface-border bg-surface-canvas overflow-hidden">
                                      {band && <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${bandColorCls ?? 'bg-text-muted'}`} />}
                                      <button
                                        type="button"
                                        disabled={!canUpdate && !canDelete}
                                        onClick={() => setEditorTarget({ mode: 'edit', assignment: a })}
                                        className={`w-full text-left px-1 py-1 hover:border-brand-red disabled:cursor-default ${band ? 'pl-2' : ''}`}
                                      >
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <span className="tabular-nums">{isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}</span>
                                          {a.is_locked && <Lock size={10} aria-hidden className="shrink-0 text-text-sub" />}
                                          {/* design-expert Critical指摘: icon_key 未設定の時間帯区分は色バーのみが唯一の
                                              視覚差分になり「色だけで区別しない」原則に反する。アイコン未設定時は
                                              ラベルの先頭1文字を可視テキストとして必ず表示する (sr-only では
                                              晴眼者に伝わらない)。 */}
                                          {band && (BandIcon
                                            ? <BandIcon size={9} aria-hidden className="shrink-0 text-text-sub" />
                                            : <span aria-hidden className="shrink-0 text-[9px] leading-none font-semibold text-text-sub">{band.label.slice(0, 1)}</span>)}
                                        </div>
                                        {band && <span className="sr-only">時間帯: {band.label}</span>}
                                        <OriginBadge origin={a.origin} />
                                      </button>
                                      {a.origin === 'auto_generated' && (
                                        <button
                                          type="button"
                                          onClick={() => setReasonsAssignmentId(a.assignment_id)}
                                          className="w-full flex items-center justify-center gap-0.5 text-[10px] text-text-sub border-t border-surface-border py-0.5 hover:text-brand-red"
                                        >
                                          <Info size={10} aria-hidden />根拠
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                                {canCreate && (
                                  <button
                                    type="button"
                                    aria-label={`${row.displayName}の${formatDateLabel(date)}にシフトを追加`}
                                    onClick={() => setEditorTarget({ mode: 'create', person: { employee_id: row.ref.employee_id, external_person_id: row.ref.external_person_id, display_name: row.displayName }, workDate: date, homeNodeId: row.homeNodeId })}
                                    className="min-h-[28px] flex items-center justify-center rounded border border-dashed border-surface-border text-text-sub hover:border-brand-red hover:text-brand-red"
                                  >
                                    <Plus size={12} aria-hidden />
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* <768px: 日付ドリル型 */}
              <div className="md:hidden">
                <div className="flex overflow-x-auto gap-1.5 p-2 border-b border-surface-border">
                  {dateList.map((date) => {
                    const cov = dayCoverage(date);
                    const active = selectedDate ? selectedDate === date : date === dateList[0];
                    // ⚠️ active 時は navy 背景に白文字のため、曜日色トークン (白/sunken/canvas 背景でのみ
                    // コントラスト実測済み) を重ねない (D-234 §1-2(b))。非 active 時のみ適用する。
                    const kind = resolveDayKind(date, holidays);
                    const holidayName = holidayNameOf(date, holidays);
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => setSelectedDate(date)}
                        title={holidayName ?? undefined}
                        className={`shrink-0 min-h-[44px] px-2.5 rounded-full text-xs border ${
                          active ? 'bg-misespark-navy text-white border-misespark-navy' : `border-surface-border ${dayTextClass(kind) || 'text-text-body'}`
                        }`}
                      >
                        {formatDateLabel(date, { withWeekday: true })}
                        {holidayName && <span aria-hidden="true">・</span>}
                        {holidayName && <span className="sr-only">（{holidayName}）</span>}
                        {cov !== 'ok' && <AlertTriangle size={10} aria-hidden className="inline ml-1 -mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const date = selectedDate ?? dateList[0];
                  if (!date) return <div className="p-4 text-sm text-text-sub">この期間に日付がありません。</div>;
                  const cov = dayCoverage(date);
                  return (
                    <div className="p-3 space-y-2">
                      {cov !== 'ok' && (
                        <div className="text-xs text-alert-text flex items-center gap-1"><AlertTriangle size={12} aria-hidden />
                          {cov === 'shortage' ? '人員が不足しています' : cov === 'excess' ? '人員が過剰です' : '警告があります'}
                        </div>
                      )}
                      {rows.length === 0 && (
                        <p className="text-sm text-text-sub text-center py-6">この店舗にはまだ従業員が割り当てられていません。</p>
                      )}
                      {rows.length > 0 && filteredRows.length === 0 && (
                        <p className="text-sm text-text-sub text-center py-6">絞り込み条件に一致する従業員がいません。</p>
                      )}
                      {filteredRows.map((row) => {
                        const cellAssignments = assignmentsByCell.get(`${row.key}|${date}`) ?? [];
                        const stat = rowStats.get(row.key);
                        return (
                          <div key={row.key} className="border border-surface-border rounded-lg p-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-sm font-semibold text-text-body">{row.displayName}</div>
                              {(() => {
                                const fulfillment = contractedDaysFulfillment.get(row.key);
                                if (!fulfillment) return null;
                                return <ContractedDaysBadge workedDays={fulfillment.workedDays} target={fulfillment.target} sourceKind={fulfillment.sourceKind} />;
                              })()}
                              {stat && stat.totalMinutes > 0 && (
                                <span className="text-[10px] text-text-sub tabular-nums">計 {formatLaborMinutes(stat.totalMinutes)}</span>
                              )}
                            </div>
                            <div className="mt-1 space-y-1">
                              {cellAssignments.map((a) => {
                                const band = showTimeBands ? resolveTimeBand(isoToLocalHm(a.start_at), timeBands) : null;
                                const BandIcon = band?.icon_key ? TIME_BAND_ICON_COMPONENTS[band.icon_key] : null;
                                const bandColorCls = band?.color_token ? TIME_BAND_COLOR_CLASSES[band.color_token] : null;
                                return (
                                  <div key={a.assignment_id} className="relative rounded border border-surface-border overflow-hidden">
                                    {band && <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${bandColorCls ?? 'bg-text-muted'}`} />}
                                    <button
                                      type="button" disabled={!canUpdate && !canDelete}
                                      onClick={() => setEditorTarget({ mode: 'edit', assignment: a })}
                                      className={`w-full min-h-[44px] text-left text-xs px-2 py-1.5 flex items-center justify-between gap-2 disabled:cursor-default ${band ? 'pl-3' : ''}`}
                                    >
                                      <span className="tabular-nums flex items-center gap-1">
                                        {isoToLocalHm(a.start_at)}〜{isoToLocalHm(a.end_at)}
                                        {a.is_locked && <Lock size={10} aria-hidden className="shrink-0 text-text-sub" />}
                                        {/* design-expert Critical指摘: icon_key 未設定時も色以外の視覚差分を残す */}
                                        {band && (BandIcon
                                          ? <BandIcon size={10} aria-hidden className="shrink-0 text-text-sub" />
                                          : <span aria-hidden className="shrink-0 text-[10px] leading-none font-semibold text-text-sub">{band.label.slice(0, 1)}</span>)}
                                      </span>
                                      <OriginBadge origin={a.origin} />
                                      {band && <span className="sr-only">時間帯: {band.label}</span>}
                                    </button>
                                    {a.origin === 'auto_generated' && (
                                      // design-expert M-7: 上の編集ボタンと隣接しすぎて誤タップしやすいため、
                                      // 背景色で明確にゾーンを分ける (境界線1pxだけに頼らない)
                                      <button
                                        type="button"
                                        onClick={() => setReasonsAssignmentId(a.assignment_id)}
                                        className="w-full min-h-[44px] flex items-center justify-center gap-0.5 text-[10px] text-text-sub bg-surface-sunken border-t border-surface-border py-1 hover:text-brand-red"
                                      >
                                        <Info size={10} aria-hidden />根拠を見る
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                              {canCreate && (
                                <Button
                                  variant="ghost" size="sm" leftIcon={<Plus size={12} />}
                                  onClick={() => setEditorTarget({ mode: 'create', person: { employee_id: row.ref.employee_id, external_person_id: row.ref.external_person_id, display_name: row.displayName }, workDate: date, homeNodeId: row.homeNodeId })}
                                >
                                  追加
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="space-y-3">
              <div className="border border-surface-border rounded-lg bg-white p-3">
                <h3 className="text-sm font-semibold text-text-body mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={14} aria-hidden className="text-text-sub" />警告
                </h3>
                <ViolationsPanel
                  violations={board.violations}
                  canAcknowledge={canUpdate}
                  onAcknowledge={(violationId, reason) => acknowledgeMut.mutate({ violationId, reason })}
                />
                {nodelessViolations.length > 0 && (
                  <p className="text-[11px] text-text-sub mt-2">うち店舗全体に関わるもの {nodelessViolations.length} 件を含みます。</p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {editorTarget && (
        <AssignmentEditorModal
          target={editorTarget} nodeId={nodeId} periodId={periodId}
          canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete}
          onClose={() => setEditorTarget(null)}
          onSaved={() => { setEditorTarget(null); invalidateBoard(); }}
          onConflict={() => { setEditorTarget(null); invalidateBoard(); }}
        />
      )}
      {reasonsAssignmentId && (
        <ReasonsModal assignmentId={reasonsAssignmentId} onClose={() => setReasonsAssignmentId(null)} />
      )}
    </div>
  );
}
